import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { useUser } from '../context/UserContext';
import userService from '../services/userService';
import { fetchScipQuery } from '../components/api';

const specialScopeIso3 = new Set(['HRV']);
const specialScopeCountryNames = new Set(['hrv']);

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB');
};

const formatStatus = (stageValue) => {
  if (stageValue === null || typeof stageValue === 'undefined') return '';
  const stage = Number(stageValue);
  const map = { 0: 'Draft', 1: 'In Progress', 2: 'Submitted', 3: 'Finalized', 4: 'Locked' };
  return Number.isFinite(stage) && map[stage] ? map[stage] : String(stageValue);
};

const StepButton = ({ index, activeStep, title, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(index)}
    className={`px-4 py-2 rounded-md border text-sm ${
      activeStep === index ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
    }`}
  >
    {index}. {title}
  </button>
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
    <input
      type="text"
      value={value == null ? '' : String(value)}
      readOnly
      className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-800"
    />
  </div>
);

const getFirstTextValue = (row, preferredKeys = []) => {
  if (!row || typeof row !== 'object') return '';
  for (const key of preferredKeys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  const genericKeys = ['Text', 'Description', 'Details', 'Name', 'Title'];
  for (const key of genericKeys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
};

const SCIPToolDetail = ({ userClaims, isEmbedded = false, backHref = '/scip-tool' }) => {
  const { user } = useUser();
  const { cipId } = useParams();
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [basicInfo, setBasicInfo] = useState(null);
  const [goals, setGoals] = useState([]);
  const [targets, setTargets] = useState([]);
  const [activities, setActivities] = useState([]);

  const resolveRegionIso3 = useCallback(async () => {
    const countries = await userService.getAvailableCountries();
    const userIso3 = String(user?.country_iso3 || '').toUpperCase();
    const userCountryName = String(user?.country || '').toLowerCase();
    const current = countries.find((c) => {
      const iso3 = String(c?.iso3 || '').toUpperCase();
      const countryName = String(c?.Country_Name || '').toLowerCase();
      return (userIso3 && iso3 === userIso3) || (userCountryName && countryName === userCountryName);
    });
    const region = current?.UNICEF_RO || current?.region || current?.Region || '';
    if (!region) return [];
    const regionCountries = await userService.getCountriesByRegion(region);
    return regionCountries.map((c) => String(c?.iso3 || '').trim().toUpperCase()).filter(Boolean);
  }, [user?.country, user?.country_iso3]);

  const buildScope = useCallback(async () => {
    const accessLevel = String(user?.accessLevel || '').toLowerCase();
    let visibleIso3 = null;
    let visibleCountryNames = null;

    if (accessLevel === 'global' || accessLevel === 'admin') {
      visibleIso3 = null;
      visibleCountryNames = null;
    } else if (accessLevel === 'regional') {
      const fromUserIso = Array.isArray(user?.allowedCountriesIso3)
        ? user.allowedCountriesIso3.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean)
        : [];
      const fromUserNames = Array.isArray(user?.allowedCountries)
        ? user.allowedCountries.map((c) => String(c || '').trim()).filter(Boolean)
        : [];
      visibleIso3 = fromUserIso.length > 0 ? fromUserIso : await resolveRegionIso3();
      visibleCountryNames = fromUserNames;
    } else {
      const primaryIso3 = String(
        user?.country_iso3 || (Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3[0] : '')
      ).trim().toUpperCase();
      const primaryCountryName = String(
        user?.country || (Array.isArray(user?.allowedCountries) ? user.allowedCountries[0] : '')
      ).trim();
      visibleIso3 = primaryIso3 ? [primaryIso3] : [];
      visibleCountryNames = primaryCountryName ? [primaryCountryName] : [];
    }

    if (Array.isArray(visibleIso3)) {
      visibleIso3 = visibleIso3.filter((iso3) => !specialScopeIso3.has(String(iso3 || '').toUpperCase()));
    }
    if (Array.isArray(visibleCountryNames)) {
      visibleCountryNames = visibleCountryNames.filter(
        (name) => !specialScopeCountryNames.has(String(name || '').trim().toLowerCase())
      );
    }

    const hasIsoScope = Array.isArray(visibleIso3) && visibleIso3.length > 0;
    const hasNameScope = Array.isArray(visibleCountryNames) && visibleCountryNames.length > 0;
    if ((Array.isArray(visibleIso3) || Array.isArray(visibleCountryNames)) && !hasIsoScope && !hasNameScope) {
      return { clause: '', params: {} };
    }

    const params = {};
    const filterParts = [];
    if (hasIsoScope) {
      const isoPlaceholders = visibleIso3.map((_, idx) => `@iso3_${idx}`).join(', ');
      filterParts.push(`CM.IsoCode IN (${isoPlaceholders})`);
      visibleIso3.forEach((iso3, idx) => {
        params[`iso3_${idx}`] = iso3;
      });
    }
    if (hasNameScope) {
      const namePlaceholders = visibleCountryNames.map((_, idx) => `@country_${idx}`).join(', ');
      filterParts.push(`CM.Name IN (${namePlaceholders})`);
      visibleCountryNames.forEach((name, idx) => {
        params[`country_${idx}`] = name;
      });
    }
    return { clause: filterParts.length ? ` AND (${filterParts.join(' OR ')})` : '', params };
  }, [resolveRegionIso3, user]);

  const fetchByPossibleKeys = useCallback(async (tableName, selectedCipId, possibleKeys) => {
    for (const key of possibleKeys) {
      try {
        const query = `SELECT * FROM SCIP.${tableName} WHERE ${key} = @cipId ORDER BY Id`;
        const rowsResult = await fetchScipQuery(query, { cipId: selectedCipId });
        return Array.isArray(rowsResult) ? rowsResult : [];
      } catch (err) {
        const msg = String(err?.message || '');
        if (!msg.includes('Invalid column name')) throw err;
      }
    }
    return [];
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!cipId || !user) return;
      setLoading(true);
      setError('');
      try {
        const scope = await buildScope();
        const query = `
          SELECT TOP 1
            CBI.Id,
            CBI.Name,
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3,
            CM.UnicefRegionName,
            CM.WhoRegionCode,
            AT.Val AS AssessmentType,
            PF.Val AS ProgrammeFocus,
            ASCP.Val AS AssessmentScope,
            CBI.StartDate,
            CBI.DurationInYear,
            CBI.FinalizedDate,
            CBI.Stage,
            CBI.LockDate,
            CBI.IsPermenentlyLock,
            CBI.RejectedCount,
            CBI.UpdatedDate,
            CBI.UpdatedBy
          FROM SCIP.CipBasicInformation CBI
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CBI.CountryId
          LEFT JOIN SCIP.GenericDataMaster GMAT ON GMAT.Id = CBI.AssessmentTypeId
          LEFT JOIN SCIP.GenericDataMaster GMPF ON GMPF.Id = CBI.ProgrammeFocusId
          LEFT JOIN SCIP.GenericDataMaster GMAS ON GMAS.Id = CBI.AssessmentScopeId
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMAT.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) AT
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMPF.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) PF
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMAS.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) ASCP
          WHERE CBI.Id = @cipId
          ${scope.clause}
        `;

        const detailRows = await fetchScipQuery(query, { cipId: Number(cipId), ...scope.params });
        if (!Array.isArray(detailRows) || !detailRows[0]) {
          throw new Error('CIP not found or not accessible in your scope');
        }

        const selectedId = Number(detailRows[0].Id);
        const [goalsRows, targetsRows, activitiesRows] = await Promise.all([
          fetchByPossibleKeys('CipGoal', selectedId, ['CipBasicInformationId', 'CipId']),
          fetchByPossibleKeys('CipTarget', selectedId, ['CipBasicInformationId', 'CipId']),
          fetchByPossibleKeys('CipActivity', selectedId, ['CipBasicInformationId', 'CipId'])
        ]);

        if (!mounted) return;
        setBasicInfo(detailRows[0]);
        setGoals(goalsRows);
        setTargets(targetsRows);
        setActivities(activitiesRows);
      } catch (e) {
        if (mounted) setError(e?.message || 'Failed to load CIP details');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [cipId, user, buildScope, fetchByPossibleKeys]);

  const visionText = useMemo(() => {
    const fromBasic = basicInfo?.Vision || basicInfo?.VisionText || basicInfo?.Goal || '';
    if (fromBasic) return String(fromBasic);
    const firstGoal = Array.isArray(goals) && goals[0] ? goals[0] : null;
    return getFirstTextValue(firstGoal, ['GoalsText', 'GoalText', 'VisionText']);
  }, [basicInfo, goals]);

  const goalsTextRows = useMemo(
    () =>
      (Array.isArray(goals) ? goals : [])
        .map((g) => getFirstTextValue(g, ['GoalsText', 'GoalText', 'VisionAndGoalsText', 'VisionText']))
        .filter(Boolean),
    [goals]
  );

  const targetsTextRows = useMemo(
    () =>
      (Array.isArray(targets) ? targets : [])
        .map((t) => getFirstTextValue(t, ['OperationalPlanText', 'TargetText', 'TargetsText', 'GoalsText']))
        .filter(Boolean),
    [targets]
  );

  const activitiesTextRows = useMemo(
    () =>
      (Array.isArray(activities) ? activities : [])
        .map((a) => getFirstTextValue(a, ['OperationalPlanText', 'ActivityText', 'ActivitiesText', 'GoalsText']))
        .filter(Boolean),
    [activities]
  );

  const content = (
    <div className={isEmbedded ? '' : 'min-h-screen bg-gray-100'}>
      {!isEmbedded && <TopBar currentPage="scip-tool" userClaims={userClaims} />}
      <div className={isEmbedded ? 'space-y-4' : 'p-4 space-y-4'}>
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">CIP Details</h2>
              <div className="text-sm text-gray-500">Three-step form view</div>
            </div>
            <a href={backHref} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md">Back to SCIP Tool</a>
          </div>
        </div>

        {loading && <div className="bg-white shadow-md rounded-lg p-6 text-gray-600">Loading CIP details...</div>}
        {error && <div className="bg-white shadow-md rounded-lg p-6 text-red-600">{error}</div>}

        {!loading && !error && basicInfo && (
          <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              <StepButton index={1} activeStep={activeStep} title="Basic Information" onClick={setActiveStep} />
              <StepButton index={2} activeStep={activeStep} title="Vision & Goals" onClick={setActiveStep} />
              <StepButton index={3} activeStep={activeStep} title="Operational Plans" onClick={setActiveStep} />
            </div>

            {activeStep === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ReadOnlyField label="Name" value={basicInfo.Name} />
                <ReadOnlyField label="Country" value={basicInfo.CountryName} />
                <ReadOnlyField label="ISO3" value={basicInfo.ISO3} />
                <ReadOnlyField label="UNICEF Region" value={basicInfo.UnicefRegionName} />
                <ReadOnlyField label="WHO Region" value={basicInfo.WhoRegionCode} />
                <ReadOnlyField label="Assessment Type" value={basicInfo.AssessmentType} />
                <ReadOnlyField label="Programme Focus" value={basicInfo.ProgrammeFocus} />
                <ReadOnlyField label="Assessment Scope" value={basicInfo.AssessmentScope} />
                <ReadOnlyField label="Start Date" value={formatDate(basicInfo.StartDate)} />
                <ReadOnlyField label="Duration (years)" value={basicInfo.DurationInYear} />
                <ReadOnlyField label="Finalization Date" value={formatDate(basicInfo.FinalizedDate)} />
                <ReadOnlyField label="Status" value={formatStatus(basicInfo.Stage)} />
                <ReadOnlyField label="Last Updated" value={formatDate(basicInfo.UpdatedDate)} />
                <ReadOnlyField label="Updated By" value={basicInfo.UpdatedBy} />
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Vision</label>
                  <textarea readOnly className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-800 min-h-[120px]" value={visionText} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Goals ({goalsTextRows.length})</label>
                  <div className="space-y-2 max-h-[40vh] overflow-auto">
                    {goalsTextRows.length === 0 && (
                      <div className="border rounded p-3 bg-gray-50 text-gray-500 text-sm">No goal text available.</div>
                    )}
                    {goalsTextRows.map((text, idx) => (
                      <div key={`goal-text-${idx}`} className="border rounded p-3 bg-gray-50">
                        <div className="text-xs text-gray-500 mb-1">Goal {idx + 1}</div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Targets ({targetsTextRows.length})</label>
                  <div className="space-y-2 max-h-[30vh] overflow-auto">
                    {targetsTextRows.length === 0 && (
                      <div className="border rounded p-3 bg-gray-50 text-gray-500 text-sm">No target text available.</div>
                    )}
                    {targetsTextRows.map((text, idx) => (
                      <div key={`target-text-${idx}`} className="border rounded p-3 bg-gray-50">
                        <div className="text-xs text-gray-500 mb-1">Target {idx + 1}</div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Activities ({activitiesTextRows.length})</label>
                  <div className="space-y-2 max-h-[30vh] overflow-auto">
                    {activitiesTextRows.length === 0 && (
                      <div className="border rounded p-3 bg-gray-50 text-gray-500 text-sm">No activity text available.</div>
                    )}
                    {activitiesTextRows.map((text, idx) => (
                      <div key={`activity-text-${idx}`} className="border rounded p-3 bg-gray-50">
                        <div className="text-xs text-gray-500 mb-1">Activity {idx + 1}</div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return content;
};

export default SCIPToolDetail;

